import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Translation } from 'react-i18next';
import {
  MultiSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import { titleize, unescapeHTML, humanize } from 'underscore.string';
import { isEqual, orderBy, first, startCase, isObject, isNil } from 'lodash';
import _ from 'underscore';

import { getEquipmentTypesForShipmentTypes } from 'constants/EquipmentTypes';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import Alert from 'components/ui/Alert';
import { RAIL, DRAYAGE } from 'constants/ShipmentTypes';
import ShipmentDetailValueItem from './details/ShipmentDetailValueItem';
import ShipmentDetailDateItem from './details/ShipmentDetailDateItem';
import ShipmentDetailDropdown from './details/ShipmentDetailDropDown';

import EditReferenceNumbersModal from './EditReferenceNumbersModal';
import TagEditor from '../../ui/TagEditor';
import Constants from '../../../constants/AdminConstants';
import CargomaticCollapsibleCard from '../../v2/CollapsibleCard';
import DescriptionListItem from '../../v2/DescriptionListItem';
import CargomaticInput from '../../v2/input';
import InfoIcon from '../../v2/icons/InfoIcon';
import EditButton from '../../v2/buttons/EditButton';
import Badge from '../../v2/Badge';
import { isIntermodalShipmentType } from '../../../helpers/shipment-types';
import {
  LOADED_ON_RAIL,
  DEPARTED_ON_RAIL,
  ARRIVED_AT_RAIL_FACILITY,
  UNLOADED_FROM_RAIL,
  AVAILABLE_AT_RAIL_FACILITY,
  FULLY_OUTGATED_FROM_RAIL_FACILITY,
  FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  LOADED_AT_RAIL_ORIGIN,
  DEPARTED_FROM_ORIGIN_RAIL,
  ARRIVED_AT_DESTINATION_RAIL_FACILITY,
  UNLOADED_AT_DESTINATION_PORT,
  RAIL_EARLIEST_RETURN_DATE,
  RAIL_CUT_DATE,
} from '../../../helpers/events';
import { importantShipmentDates } from './mappings';
import { getDocumentsV2 } from '../../../utils/DocumentsAPIUtils';
import ShipmentDetailIndividualItem from './details/ShipmentDetailIndividualItem';
import OrdersStore from 'stores/OrdersStore';
import { mapTags } from 'utils/formatters/tags';
import OrderDetailsDisplay from './details/OrderDetailsDisplay';
import { getShipmentTimezone } from 'helpers/shipments';
import { MASTER_BOL_STATUS } from 'constants/Opentrack';

const nonEditableImportantDates = [
  'original_dispatch_date',
  'current_dispatch_date',
];

const showDateFieldsForDrayageOnly = ['planned_delivery_date'];

const SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS = [
  {
    localizationSlug: 'POD_VALID',
    value: 'POD Valid',
    selectable: true,
  },
  {
    localizationSlug: 'POD_INVALID',
    value: 'POD Invalid',
    selectable: true,
  },
  {
    localizationSlug: 'NEED_VERIFICATION',
    value: 'Needs verification',
    selectable: false,
  },
];

const getDropDownValue = (isPodValid) => {
  const [valid, invalid, needsVerification] =
    SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS;
  if (isPodValid === true) {
    return valid;
  }
  if (isPodValid === false) {
    return invalid;
  }
  return needsVerification;
};

const INTERMODAL_SHIPMENT_TYPES = [
  {
    localizationSlug: 'DRAYAGE',
    value: DRAYAGE,
    titleize: true,
  },
  {
    localizationSlug: 'RAIL',
    value: RAIL,
    titleize: true,
  },
];
const NON_INTERMODAL_SHIPMENT_TYPES = [
  {
    localizationSlug: 'SHORTHAUL',
    value: 'shorthaul',
    titleize: true,
  },
  {
    localizationSlug: 'LONGHAUL',
    value: 'longhaul',
    titleize: true,
  },
  {
    localizationSlug: 'DAYRATE',
    value: 'dayrate',
    titleize: true,
  },
];

// Canonical customer_service_type values, matching the backend enum on the Job
// schema (app/models/schema/v2/job.schema.js). The trailing null item is both the
// display fallback when the shipment has no customer_service_type and a selectable
// option so the value can be cleared back to null.
const CUSTOMER_SERVICE_TYPE_DROPDOWN_ITEMS = [
  { localizationSlug: 'NSR', value: 'NSR', selectable: true },
  { localizationSlug: 'ROC', value: 'ROC', selectable: true },
  { localizationSlug: 'IDD', value: 'IDD', selectable: true },
  { localizationSlug: 'WGD/WWA', value: 'WGD/WWA', selectable: true },
  { localizationSlug: 'NOT_SET', value: null, selectable: true },
];

const getCustomerServiceTypeValue = (customerServiceType) =>
  CUSTOMER_SERVICE_TYPE_DROPDOWN_ITEMS.find(
    ({ value }) => value === customerServiceType,
  ) ||
  CUSTOMER_SERVICE_TYPE_DROPDOWN_ITEMS[
    CUSTOMER_SERVICE_TYPE_DROPDOWN_ITEMS.length - 1
  ];

const determineEditableShipmentTypes = (isIntermodalType) => {
  return isIntermodalType
    ? INTERMODAL_SHIPMENT_TYPES
    : NON_INTERMODAL_SHIPMENT_TYPES;
};

const getShipmentTypeValue = (shipmentType, items) =>
  items.find(({ value }) => value === shipmentType) || {
    localizationSlug: 'NOT_SET',
    value: null,
    selectable: false,
  };

// Exception-exempt is a boolean; expose it as a Yes/No inline-edit dropdown.
const EXCEPTION_EXEMPT_DROPDOWN_ITEMS = [
  { localizationSlug: 'YES', value: 'true', selectable: true },
  { localizationSlug: 'NO', value: 'false', selectable: true },
];

const getExceptionExemptValue = (exempt) =>
  EXCEPTION_EXEMPT_DROPDOWN_ITEMS.find(
    ({ value }) => value === String(!!exempt),
  );

const ShipmentDetails = (props) => {
  const { t } = useTranslation();
  const {
    shipment,
    onReferenceNumbersChange,
    onNotesUpdate,
    onAdminNotesUpdate,
    onTagsUpdate,
    onUpdateEstimatedArrivalChange,
    onUpdateArrivalDateChange,
    onUpdateDischargeDateChange,
    onUpdateCustomLfdChange,
    onUpdateLastFreeDateChange,
    onUpdateYardLastFreeDateChange,
    onUpdateEmptyReturnedDateChange,
    onUpdateEmptyReadyForPickupDateChange,
    onUpdateLastCutDateChange,
    onUpdateEarliestReturnDateChange,
    onUpdateEarliestEmptyDateChange,
    onUpdateOutgatedDateChange,
    onUpdateCfsGateInChange,
    onUpdateCfsGateOutChange,
    onShipmentTypeChange,
    onCustomerServiceTypeChange,
    onEquipmentTypeChange,
    onExceptionExemptChange,
    onUpdateRailEvent,
    onPODValidChange,
    onStackPositionUpdate,
    updateMissingReferenceNumbers,
    onUpdateAvailableDateChange,
    onUpdatePerDiemDateChange,
    onUpdateDocumentCutoffDateChange,
    onUpdateYardAvailableDateChange,
  } = props;

  const tags = useMemo(() => mapTags(shipment?.tags), [shipment?.tags]);

  const timezone = useMemo(() => getShipmentTimezone(shipment), [shipment]);

  const [showEditReferenceNumbersModal, setShowEditReferenceNumbersModal] =
    useState(false);
  const [equipment_type, setEquipmentType] = useState(
    shipment.equipment_type || [],
  );
  const [editingEquipment, setEditingEquipment] = useState(false);
  const [podDocuments, setPodDocuments] = useState([]);
  const [missingReferenceNumbersList, setMissingReferenceNumbersList] =
    useState([]);
  const [shipmentOrder, setShipmentOrder] = useState(null);

  const orderId = useMemo(
    () => (isObject(shipment?.order) ? shipment?.order?._id : shipment?.order),
    [shipment],
  );

  const order = useMemo(() => {
    if (isNil(shipment)) {
      return {};
    }

    if (!isNil(shipmentOrder)) {
      return shipmentOrder;
    }

    return isObject(shipment?.order) ? shipment?.order : {};
  }, [shipment, shipmentOrder]);

  const isIntermodalType = useMemo(
    () => isIntermodalShipmentType(shipment),
    [shipment],
  );

  const editableShipmentTypes = useMemo(
    () => determineEditableShipmentTypes(isIntermodalType),
    [isIntermodalType],
  );

  const shipmentTypeDropdownItems = useMemo(
    () =>
      editableShipmentTypes.map((type) => ({
        localizationSlug: type.localizationSlug,
        value: type.value,
        selectable: true,
      })),
    [editableShipmentTypes],
  );

  useEffect(() => {
    if (orderId) {
      OrdersStore.addChangeListener(_onOrderChange);
    }

    return () => {
      if (orderId) {
        OrdersStore.removeChangeListener(_onOrderChange);
      }
    };
  }, []);

  useEffect(() => {
    if (orderId) {
      OrdersActionCreators.getOrder(orderId);
    }

    const shipmentId = shipment?._id;
    const fetchData = async () => {
      try {
        const podDocuments = await getDocuments(shipmentId);

        let missingReferenceNumbersList = [];
        if (isIntermodalType) {
          const { missingReferenceNumbers = [] } =
            await ShipmentWebAPIUtils.getMissingReferenceNumbersList(shipment);
          missingReferenceNumbersList = missingReferenceNumbers;
        }

        setPodDocuments(
          podDocuments.filter(({ status }) => status === 'UPLOADED'),
        );
        setMissingReferenceNumbersList(missingReferenceNumbersList);

        if (updateMissingReferenceNumbers) {
          updateMissingReferenceNumbers(missingReferenceNumbersList);
        }
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();
  }, [shipment, orderId]);

  useEffect(() => {
    if (!isEqual(shipment.equipment_type, equipment_type)) {
      setEquipmentType(shipment.equipment_type || []);
    }
  }, [shipment.equipment_type]);

  const _onOrderChange = () => {
    const order = OrdersStore.getOrder();

    setShipmentOrder(order);
  };

  const getDocuments = async (shipmentId) => {
    try {
      const response = await getDocumentsV2([shipmentId], {
        types: 'proof_of_delivery',
      });
      return response.data?.getDocumentsV2?.documents || [];
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const handleEditReferenceNumbers = () =>
    setShowEditReferenceNumbersModal(true);
  const handleCloseEditReferenceNumbersModal = () =>
    setShowEditReferenceNumbersModal(false);

  const handleSaveReferenceNumbers = (
    referenceNumbers,
    updatedRefEvents = [],
  ) => {
    onReferenceNumbersChange(shipment, referenceNumbers, updatedRefEvents);
    handleCloseEditReferenceNumbersModal();
  };

  const handleEquipmentTypeChange = (selectedOptions) => {
    setEquipmentType((selectedOptions || []).map((option) => option.value));
  };

  const handleEquipmentTypeSave = () => {
    onEquipmentTypeChange(equipment_type);
    setEditingEquipment(false);
  };

  const handleEquipmentTypeCancel = () => {
    setEquipmentType(shipment.equipment_type || []);
    setEditingEquipment(false);
  };

  const getDeliveryOrderStatus = () => {
    const { delivery_order_status } = shipment;

    if (shipment) {
      return delivery_order_status === 'not-sent' || !delivery_order_status ? (
        <Badge dataTestId="Badge-f03af3d8" color="red">
          {t('NOT_SENT')}
        </Badge>
      ) : (
        <Badge dataTestId="Badge-439e12b0" color="brand">
          {t(sluggify(delivery_order_status))}
        </Badge>
      );
    }
  };

  const renderReferenceNumbers = () => {
    const { reference_numbers } = shipment;
    const orderType = order?.order_type;

    if (!reference_numbers || reference_numbers.length === 0) {
      return <p data-testid="p-c5d8db35">{t('NO_REFERENCE_NUMBERS')}</p>;
    }

    const refNumList = reference_numbers
      .filter((refNum) => !refNum.stop_id)
      .map((refNum, i) => {
        const { name, value } = refNum;
        const label = t(sluggify(unescapeHTML(name)));
        const descriptionListItemProps = {
          dataTestId: `DescriptionListItem-26766dd0-${i}`,
          className: 'mb-4',
          label,
        };

        // Badge for Master BOL Validation
        if (
          name === 'master_bol' &&
          isIntermodalType &&
          orderType !== 'export'
        ) {
          const { badgeColor, badgeInfo } = getMasterBolBadgeData();
          return (
            <DescriptionListItem
              key={i}
              {...descriptionListItemProps}
              badgeColor={badgeColor}
              badgeInfo={badgeInfo}
            >
              {unescapeHTML(value)}
            </DescriptionListItem>
          );
        }

        return (
          <DescriptionListItem key={i} {...descriptionListItemProps}>
            {unescapeHTML(value)}
          </DescriptionListItem>
        );
      });

    return (
      <div
        data-testid="div-2f87f7b9"
        className="sm:grid sm:grid-cols-2 sm:gap-4"
      >
        {refNumList}
      </div>
    );
  };

  const getMasterBolBadgeData = () => {
    const { opentrack_metadata } = shipment;
    if (opentrack_metadata?.master_bol_status) {
      const { master_bol_status: masterBolStatus } = opentrack_metadata;
      if (masterBolStatus === MASTER_BOL_STATUS.CONFRIMED) {
        return {
          badgeInfo: t('CONFIRMED'),
          badgeColor: 'green',
        };
      }
      return {
        badgeInfo: t(masterBolStatus.toUpperCase()),
        badgeColor: 'red',
      };
    }
    return {};
  };

  const renderMissingReferenceNumbers = () => {
    if (missingReferenceNumbersList.length) {
      return (
        <Alert
          dataTestId="Alert-8503592c"
          title={t('PLEASE_PROVIDE_REFERENCE_NUMBERS')}
        >
          <ul data-testid="ul-25265778">
            {missingReferenceNumbersList.map(
              (missingReferenceNumber, index) => (
                <li
                  data-testid={`li-417281a9-${index}`}
                  key={missingReferenceNumber}
                >
                  {t(missingReferenceNumber?.toUpperCase())}
                </li>
              ),
            )}
          </ul>
        </Alert>
      );
    }
  };

  const renderOrderLink = () => {
    if (!orderId) {
      return null;
    }

    const orderLink = `/orders/${orderId}`;
    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-a4bd4d92"
        label={t('ORDER')}
        className="sm:col-span-1"
      >
        <a
          data-testid="a-726d2d57"
          href={orderLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 cursor-pointer hover:text-blue-600"
        >
          <span data-testid="span-5b9a35c0">{orderId}</span>
        </a>
      </DescriptionListItem>
    );
  };

  const renderEquipmentTypes = () => {
    const selectedShipmentTypes = Array.isArray(shipment.shipment_type)
      ? shipment.shipment_type
      : [shipment.shipment_type];

    const equipmentOptions = getEquipmentTypesForShipmentTypes(
      selectedShipmentTypes,
    ).map((type) => ({
      value: type.label,
      label: t(type.label),
    }));

    const selectedOptions = equipmentOptions.filter((option) =>
      equipment_type.includes(option.value),
    );

    if (!editingEquipment) {
      return (
        <div className="flex items-center space-x-2">
          <span data-testid="equipment-type-value">
            {selectedOptions.length
              ? selectedOptions.map((option) => option.label).join(', ')
              : t('NOT_SET')}
          </span>
          <EditButton
            onClick={() => setEditingEquipment(true)}
            dataTestId="edit-equipment-type"
          />
        </div>
      );
    }

    return (
      <div data-testid="div-6091c9a9" className="mt-2 max-w-xs">
        <MultiSelect
          options={equipmentOptions}
          value={selectedOptions}
          onChange={handleEquipmentTypeChange}
          placeholder={t('SELECT_EQUIPMENT_TYPES')}
          searchPlaceholder={t('SEARCH')}
        />
        <div
          data-testid="div-2d77fc7b"
          className="flex flex-row justify-end mt-2 space-x-2"
        >
          <button
            data-testid="button-c3393514"
            onClick={handleEquipmentTypeSave}
            className="button button-green button-small"
          >
            {titleize(t('SAVE'))}
          </button>
          <button
            data-testid="cancel-equipment-type"
            onClick={handleEquipmentTypeCancel}
            className="button button-small"
          >
            {t('CANCEL')}
          </button>
        </div>
      </div>
    );
  };

  const getOnHold = () => {
    const { holds } = shipment;
    if (!holds || !holds.length) {
      return null;
    }

    const unresolvedHolds = holds.filter((hold) => {
      return !hold.resolved_at;
    });
    const lastUnresolved = _.last(unresolvedHolds);
    return lastUnresolved?.reported_at;
  };

  const renderImportantDates = () => {
    const { shipment } = props;
    return (
      <CargomaticCollapsibleCard
        id="important-dates"
        dataTestId="CargomaticCollapsibleCard-1c577a2a"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-dda0e314"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-3f1f4a2e"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-eef0d926" className="my-0">
                {t('IMPORTANT_DATES')}
              </h3>
            </div>
          </div>
        )}
      >
        <div
          data-testid="div-85a8eda4"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          {importantShipmentDates.map(({ slug, localizationSlug }) => {
            const canEdit = !nonEditableImportantDates.includes(slug);
            if (
              !isIntermodalType &&
              showDateFieldsForDrayageOnly.includes(slug)
            )
              return null;
            return (
              <ShipmentDetailDateItem
                dataTestId={`ShipmentDetailDateItem-${slug}`}
                className="mb-4 sm:col-span-1"
                title={t(`${localizationSlug}`)}
                key={humanize(`${localizationSlug}`)}
                value={shipment?.[slug]}
                editable={canEdit}
                onSave={props.onUpdateImportantDates}
                param={slug}
                useNamedUpdate
                timezone={timezone}
              />
            );
          })}
        </div>
      </CargomaticCollapsibleCard>
    );
  };

  const getUpdateStatusBadge = (eventName) => {
    const { audits } = props;

    if (!shipment[eventName] || !audits || !audits.length) {
      return null;
    }

    const events = audits.filter((audit) => {
      const { event } = audit;
      if (event === eventName || event === `${eventName}_changed`) {
        return audit;
      }
      return false;
    });

    if (!events.length) {
      return null;
    }
    const mostRecentUpdate = first(orderBy(events, ['timestamp'], ['desc']));
    const { user } = mostRecentUpdate;
    if (
      user._type === 'Admin' &&
      user.email === 'integrations@cargomatic.com'
    ) {
      return 'OPENTRACK';
    }
    if (user._type === 'Admin') {
      return 'ADMIN';
    }
    if (user._type === 'Carrier') {
      return 'CARRIER';
    }
    return null;
  };

  const getNonInterModalDetailCard = () => {
    const { shipment } = props;
    const podUploaded = !!podDocuments.length;

    return (
      <CargomaticCollapsibleCard
        id="shipment-details"
        dataTestId="CargomaticCard-b9f397db"
        header={() => (
          <div className="flex items-center justify-between">
            <h3 className="my-0">{t('SHIPMENT_DETAILS')}</h3>
          </div>
        )}
      >
        <div
          data-testid="div-bf5ec193"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          {renderOrderLink()}
          <DescriptionListItem
            dataTestId="DescriptionListItem-11f49bcd"
            label={t('B2B')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.business ? t('YES') : t('NO')}
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-2ca59407"
            label={t('AUTO_EXPOSURE')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.hold_at_dispatch ? t('DISABLED') : t('ENABLED')}
          </DescriptionListItem>
          <DescriptionListItem
            dataTestId="DescriptionListItem-exception-exempt"
            className="mb-4 sm:col-span-1"
            label={t('EXCEPTION_EXEMPT')}
          >
            <ShipmentDetailDropdown
              items={EXCEPTION_EXEMPT_DROPDOWN_ITEMS}
              value={getExceptionExemptValue(shipment.exception_exempt)}
              onSave={(item) => onExceptionExemptChange(item?.value === 'true')}
              editable
            />
          </DescriptionListItem>
          {order && (
            <>
              <DescriptionListItem
                dataTestId="DescriptionListItem-4aef2fa0"
                label={t('VESSEL')}
                className="mb-4 sm:col-span-1"
              >
                {order?.vessel_or_rail}
              </DescriptionListItem>
              <DescriptionListItem
                dataTestId="DescriptionListItem-f1ad50f5"
                label={startCase(t('TRAIN'))}
                className="mb-4 sm:col-span-1"
              >
                {order?.train}
              </DescriptionListItem>
            </>
          )}
          <DescriptionListItem
            dataTestId="DescriptionListItem-shipment-type"
            className="mb-4 sm:col-span-1"
            label={t('SHIPMENT_TYPE')}
          >
            <ShipmentDetailDropdown
              items={shipmentTypeDropdownItems}
              value={getShipmentTypeValue(
                shipment.shipment_type,
                shipmentTypeDropdownItems,
              )}
              placeholder={t('NOT_SET')}
              disabled={isIntermodalType}
              editable={!isIntermodalType}
              onSave={(item) => onShipmentTypeChange(item?.value)}
            />
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-customer-service-type"
            className="mb-4 sm:col-span-1"
            label={t('CUSTOMER_SERVICE_TYPE', 'Customer Service Type')}
          >
            <ShipmentDetailDropdown
              className="mb-4 sm:col-span-1 w-48"
              items={CUSTOMER_SERVICE_TYPE_DROPDOWN_ITEMS}
              value={getCustomerServiceTypeValue(
                shipment.customer_service_type,
              )}
              placeholder={t('NOT_SET')}
              onSave={onCustomerServiceTypeChange}
              editable
            />
          </DescriptionListItem>

          <div data-testid="div-addfddc1" className="equipment-type">
            <DescriptionListItem
              dataTestId="DescriptionListItem-1243a35c"
              className="sm:col-span-1"
              label={t('EQUIPMENT_TYPES')}
            >
              <div data-testid="div-b3cf7019">{renderEquipmentTypes()}</div>
            </DescriptionListItem>
          </div>

          <DescriptionListItem
            dataTestId="DescriptionListItem-8beb13ea"
            className="mb-4 sm:col-span-1"
            label={t('POD_VERIFICATION')}
          >
            {podUploaded && (
              <div data-testid="div-6da3cbfa" className="flex flex-row">
                <ShipmentDetailDropdown
                  className="mb-4 sm:col-span-1 w-48"
                  items={SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS}
                  disabled={!podUploaded}
                  value={getDropDownValue(shipment.is_pod_valid)}
                  onSave={onPODValidChange}
                  editable
                  showIcon
                  iconValue={shipment.is_pod_valid}
                />
              </div>
            )}
            {!podUploaded && (
              <span data-testid="span-e33a2e5b">{t('MISSING_POD')}</span>
            )}
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-c7808fc0"
            className="mb-4 sm:col-span-1"
            label={t('DELIVERY_ORDER_STATUS')}
          >
            {getDeliveryOrderStatus()}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('SUBSIDIARY')}
            className="mb-4 sm:col-span-1"
          >
            {shipment?.shipper?.subsidiary
              ? t(shipment?.shipper?.subsidiary)
              : t('NOT_SET')}
          </DescriptionListItem>
        </div>
      </CargomaticCollapsibleCard>
    );
  };

  const getInterModalShipmentOrderAndAdditionalCards = () => {
    const { shipment } = props;
    const podUploaded = !!podDocuments.length;

    return (
      <>
        <OrderDetailsDisplay
          shipment={shipment}
          order={order}
          isIntermodalType={isIntermodalType}
        />

        <CargomaticCollapsibleCard
          id="additional-details"
          dataTestId="CargomaticCollapsibleCard-1c577a2c"
          className="mb-6"
          header={() => (
            <div
              data-testid="div-dda0e336"
              className="flex items-center justify-between"
            >
              <div
                data-testid="div-3f1f4a4e"
                className="flex items-center space-x-1"
              >
                <h3 data-testid="h3-eef0d967" className="my-0">
                  {t('ADDITIONAL_DETAILS')}
                </h3>
              </div>
            </div>
          )}
        >
          <div
            data-testid="div-85a8edc3"
            className="sm:grid sm:grid-cols-2 sm:gap-6"
          >
            <div className="max-w-1/2">
              <ShipmentDetailIndividualItem
                dataTestId="ShipmentDetailIndividualItem-9074a8f6"
                title={t('EQUIPMENT_TYPES')}
                value={renderEquipmentTypes()}
              />
            </div>
            {shipment?.leg_type && (
              <ShipmentDetailIndividualItem
                dataTestId="ShipmentDetailIndividualItem-legtype"
                title="Leg Type"
                value={titleize(humanize(shipment.leg_type))}
              />
            )}

            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a83e"
              title={t('POD_VERIFICATION')}
              value={
                <>
                  {podUploaded && (
                    <div data-testid="div-6da3cbc7" className="flex flex-row">
                      <ShipmentDetailDropdown
                        className="mb-4 sm:col-span-1 w-48"
                        items={SHIPMENT_POD_DETAIL_DROPDOWN_ITEMS}
                        disabled={!podUploaded}
                        value={getDropDownValue(shipment.is_pod_valid)}
                        onSave={onPODValidChange}
                        editable
                        showIcon
                        iconValue={shipment.is_pod_valid}
                      />
                    </div>
                  )}
                  {!podUploaded && (
                    <span data-testid="span-e33a2e5b">{t('MISSING_POD')}</span>
                  )}
                </>
              }
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-9074a8f7"
              title={t('DELIVERY_ORDER_STATUS')}
              value={getDeliveryOrderStatus()}
            />
            <CargomaticInput
              dataTestId="CargomaticInput-3977d2be"
              className="mb-4 sm:col-span-1"
              type="checkbox"
              label={t('EXCEPTION_EXEMPT')}
              id="exception_exempt"
              name="exception_exempt"
              checked={shipment.exception_exempt}
              onChange={() => {
                onExceptionExemptChange(!shipment.exception_exempt);
              }}
            />
            <ShipmentDetailIndividualItem
              dataTestId="ShipmentDetailIndividualItem-381be34e"
              title={t('AUTO_EXPOSURE')}
              value={shipment.hold_at_dispatch ? t('DISABLED') : t('ENABLED')}
            />
          </div>
        </CargomaticCollapsibleCard>
      </>
    );
  };

  return (
    <div data-testid="div-2cc53f08" className="space-y-6">
      <CargomaticCollapsibleCard
        id="terminal-events"
        dataTestId="CargomaticCollapsibleCard-5a0b41e6"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-5e58c364"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-0ebc6813"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-4e159bc8" className="my-0">
                {t('TERMINAL_EVENTS')}
              </h3>
            </div>
          </div>
        )}
      >
        <div
          data-testid="div-bbed7891"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          <DescriptionListItem
            dataTestId="DescriptionListItem-ff4fe472"
            label={t('PORT_OF_DISCHARGE')}
            className="mb-4 sm:col-span-1"
          >
            {shipment.port_of_discharge}
          </DescriptionListItem>

          <DescriptionListItem
            dataTestId="DescriptionListItem-9da72169"
            label={titleize(t('TERMINAL'))}
            className="mb-4 sm:col-span-1"
          >
            {shipment.terminal_at_port_of_discharge}
          </DescriptionListItem>

          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-f70d5c6a"
            className="mb-4 sm:col-span-1"
            title={t('ETA')}
            key="ETA"
            value={shipment.estimated_arrival}
            editable
            onSave={onUpdateEstimatedArrivalChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-14e83387"
            className="mb-4 sm:col-span-1"
            title={t('ARRIVAL_DATE')}
            key="Arrival Date"
            value={shipment.arrival_date}
            editable
            onSave={onUpdateArrivalDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-53ca124b"
            className="mb-4 sm:col-span-1"
            title={t('DISCHARGE_DATE')}
            key="Discharge Date"
            value={shipment.discharge_date}
            editable
            onSave={onUpdateDischargeDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-f3f1ded6"
            className="mb-4 sm:col-span-1"
            title={t('CUSTOM_LFD')}
            key="Custom LFD"
            value={shipment.custom_lfd}
            editable
            onSave={onUpdateCustomLfdChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-terminal-available-date"
            className="mb-4 sm:col-span-1"
            title={t('TERMINAL_AVAILABLE_DATE')}
            key="Terminal Available Date"
            value={shipment.available_date}
            editable
            onSave={onUpdateAvailableDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-2869a6be"
            className="mb-4 sm:col-span-1"
            title={titleize(t('TERMINAL_LAST_FREE_DATE'))}
            key="Last Free Date"
            value={shipment.last_free_date}
            editable
            onSave={onUpdateLastFreeDateChange}
            badge={
              shipment.last_free_day_not_reported_by_terminal
                ? 'NOT_REPORTED'
                : null
            }
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-dc46686a"
            className="mb-4 sm:col-span-1"
            title={t('OUTGATED_DATE')}
            key="Outgated Date"
            value={shipment.outgated_date}
            editable
            onSave={onUpdateOutgatedDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-21a8d7c0"
            className="mb-4 sm:col-span-1"
            title={t('EMPTY_RETURNED_DATE')}
            key="Empty Returned Date"
            value={shipment.empty_returned_date}
            editable
            onSave={onUpdateEmptyReturnedDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-cfs-gate-in"
            className="mb-4 sm:col-span-1"
            title={t('CFS_GATE_IN')}
            key="CFS Gate In"
            value={shipment.cfs_gate_in}
            editable
            onSave={onUpdateCfsGateInChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-cfs-gate-out"
            className="mb-4 sm:col-span-1"
            title={t('CFS_GATE_OUT')}
            key="CFS Gate Out"
            value={shipment.cfs_gate_out}
            editable
            onSave={onUpdateCfsGateOutChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-6c7ef0b2"
            className="mb-4 sm:col-span-1"
            title="On Hold"
            key="On Hold"
            value={getOnHold()}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-8450bdb9"
            className="mb-4 sm:col-span-1"
            title={t('EMPTY_READY_FOR_PICKUP_DATE')}
            key="Empty Ready for Pickup Date"
            value={shipment.empty_ready_for_pickup}
            editable
            onSave={onUpdateEmptyReadyForPickupDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-590ca159"
            className="mb-4 sm:col-span-1"
            title={t('PER_DIEM_LFD')}
            key="Per Diem Last Free Date"
            value={shipment.per_diem_lfd}
            editable
            onSave={onUpdatePerDiemDateChange}
            timezone={timezone}
          />

          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-99e3e47a"
            className="mb-4 sm:col-span-1"
            title={t('EARLIEST_DATE_TO_GET_EMPTY')}
            key="Earliest Date to Get Empty"
            value={shipment.earliest_empty_date}
            editable
            onSave={onUpdateEarliestEmptyDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-3860e949"
            className="mb-4 sm:col-span-1"
            title={t('EARLIEST_RETURN_DATE')}
            key="Earliest Return Date"
            value={shipment.earliest_return_date}
            editable
            onSave={onUpdateEarliestReturnDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-237f4a02"
            className="mb-4 sm:col-span-1"
            title={t('DOCUMENT_CUTOFF_DATE')}
            key="Document Cutoff Date"
            value={shipment.document_cutoff_date}
            editable
            onSave={onUpdateDocumentCutoffDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-ba1e85b1"
            className="mb-4 sm:col-span-1"
            title={t('LAST_CUT_DATE')}
            key="Last Cut Date"
            value={shipment.last_cut_date}
            editable
            onSave={onUpdateLastCutDateChange}
            timezone={timezone}
          />
        </div>
      </CargomaticCollapsibleCard>

      <CargomaticCollapsibleCard
        id="yard-events"
        dataTestId="CargomaticCollapsibleCard-1e617adc"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-4bf421c3"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-1a517bdb"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-b0294917" className="my-0">
                {t('YARD_EVENTS')}
              </h3>
            </div>
          </div>
        )}
      >
        <div
          data-testid="div-6a7268a6"
          className="sm:grid sm:grid-cols-2 sm:gap-6"
        >
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-cd30cc72"
            className="mb-4 sm:col-span-1"
            title={t('YARD_AVAILABLE_DATE')}
            key="Yard Available Date"
            value={shipment.yard_available_date}
            editable
            onSave={onUpdateYardAvailableDateChange}
            timezone={timezone}
          />
          <ShipmentDetailDateItem
            dataTestId="ShipmentDetailDateItem-c2b8d7ea"
            className="mb-4 sm:col-span-1"
            title={t('YARD_LAST_FREE_DATE')}
            key="Yard Last Free Date"
            value={shipment.yard_last_free_date}
            editable
            onSave={onUpdateYardLastFreeDateChange}
            timezone={timezone}
          />
          <ShipmentDetailValueItem
            dataTestId="ShipmentDetailValueItem-4fa72f1b"
            title={t('STACK_POSITION')}
            value={unescapeHTML(shipment.stack_position)}
            editable
            onSave={(value) => onStackPositionUpdate(shipment, value)}
          />
        </div>
      </CargomaticCollapsibleCard>

      <CargomaticCollapsibleCard
        id="rail-events"
        dataTestId="CargomaticCollapsibleCard-c0d8f246"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-c6bdc4b7"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-2d9977cf"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-ba67f981" className="my-0">
                {t('RAIL_EVENTS')}
              </h3>
            </div>
          </div>
        )}
      >
        <Tabs defaultValue="general">
          <TabsList>
            <TabsTrigger value="general">{t('GENERAL')}</TabsTrigger>
            <TabsTrigger value="import">{t('IMPORT')}</TabsTrigger>
            <TabsTrigger value="export">{t('EXPORT')}</TabsTrigger>
          </TabsList>
          <TabsContent value="general">
            <div className="sm:grid sm:grid-cols-2 sm:gap-6 pt-4">
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-67774011"
                className="mb-4 sm:col-span-1"
                title={t('EMPTY_RAIL_BILLING')}
                key="Empty Rail Billing"
                value={shipment?.empty_rail_billing}
                editable
                onSave={onUpdateRailEvent}
                param="empty_rail_billing"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-c5ab0e0c"
                className="mb-4 sm:col-span-1"
                title={t('LOADED_RAIL_BILLING')}
                key="Loaded Rail Billing"
                value={shipment?.loaded_rail_billing}
                editable
                onSave={onUpdateRailEvent}
                param="loaded_rail_billing"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-e3750e78"
                className="mb-4 sm:col-span-1"
                title={t('LFD_AT_RAIL_FACILITY_ESTIMATED')}
                key="LFD At Rail Facility Estimated"
                value={shipment.rail_lfd_at_facility_estimated}
                editable
                onSave={onUpdateRailEvent}
                param="rail_lfd_at_facility_estimated"
                badge={getUpdateStatusBadge('rail_lfd_at_facility_estimated')}
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-859e82fe"
                className="mb-4 sm:col-span-1"
                title={t('LFD_AT_RAIL_FACILITY')}
                key="LFD At Rail Facility Actual"
                value={shipment.rail_lfd_at_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_lfd_at_facility"
                useNamedUpdate
                badge={getUpdateStatusBadge('rail_lfd_at_facility')}
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-72b78af2"
                className="mb-4 sm:col-span-1"
                title={t('ETA_TO_FINAL_RAIL_FACILITY')}
                key="ETA To Final Rail Facility"
                value={shipment.rail_eta_final_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_eta_final_facility"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-cd825ecc"
                className="mb-4 sm:col-span-1"
                title={t('PER_DIEM_LFD')}
                key="Rail Per Diem Last Free Date"
                value={shipment.rail_per_diem_lfd}
                editable
                onSave={onUpdateRailEvent}
                param="rail_per_diem_lfd"
                useNamedUpdate
                timezone={timezone}
              />
            </div>
          </TabsContent>
          <TabsContent value="import">
            <div className="sm:grid sm:grid-cols-2 sm:gap-6 pt-4">
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-4a773285"
                className="mb-4 sm:col-span-1"
                title={t(LOADED_ON_RAIL)}
                key={t(LOADED_ON_RAIL)}
                value={shipment.rail_loaded}
                editable
                onSave={onUpdateRailEvent}
                param="rail_loaded"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-fd98d812"
                className="mb-4 sm:col-span-1"
                title={t(DEPARTED_ON_RAIL)}
                key={t(DEPARTED_ON_RAIL)}
                value={shipment.rail_departed}
                editable
                onSave={onUpdateRailEvent}
                param="rail_departed"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-d4dcc803"
                className="mb-4 sm:col-span-1"
                title={t(ARRIVED_AT_RAIL_FACILITY)}
                key={t(ARRIVED_AT_RAIL_FACILITY)}
                value={shipment.rail_arrived_at_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_arrived_at_facility"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-36f5646a"
                className="mb-4 sm:col-span-1"
                title={t(UNLOADED_FROM_RAIL)}
                key={t(UNLOADED_FROM_RAIL)}
                value={shipment.rail_unloaded}
                editable
                onSave={onUpdateRailEvent}
                param="rail_unloaded"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-6cbb1385"
                className="mb-4 sm:col-span-1"
                title={t(AVAILABLE_AT_RAIL_FACILITY)}
                key={t(AVAILABLE_AT_RAIL_FACILITY)}
                value={shipment.rail_available_at_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_available_at_facility"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-dfefbff9"
                className="mb-4 sm:col-span-1"
                title={t(FULLY_OUTGATED_FROM_RAIL_FACILITY)}
                key={t(FULLY_OUTGATED_FROM_RAIL_FACILITY)}
                value={shipment.rail_outgated_from_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_outgated_from_facility"
                useNamedUpdate
                timezone={timezone}
              />
            </div>
          </TabsContent>
          <TabsContent value="export">
            <div className="sm:grid sm:grid-cols-2 sm:gap-6 pt-4">
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-9efadd70"
                className="mb-4 sm:col-span-1"
                title={t(RAIL_EARLIEST_RETURN_DATE)}
                key={t(RAIL_EARLIEST_RETURN_DATE)}
                value={shipment.rail_earliest_return}
                editable
                onSave={onUpdateRailEvent}
                param="rail_earliest_return"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-228468f6"
                className="mb-4 sm:col-span-1"
                title={t(FULL_INGATED_AT_ORIGIN_RAIL_FACILITY)}
                key={t(FULL_INGATED_AT_ORIGIN_RAIL_FACILITY)}
                value={shipment.rail_ingated_at_origin_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_ingated_at_origin_facility"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-ad08dab3"
                className="mb-4 sm:col-span-1"
                title={t(RAIL_CUT_DATE)}
                key={t(RAIL_CUT_DATE)}
                value={shipment.rail_cut_date}
                editable
                onSave={onUpdateRailEvent}
                param="rail_cut_date"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-d062ec7c"
                className="mb-4 sm:col-span-1"
                title={t(LOADED_AT_RAIL_ORIGIN)}
                key={t(LOADED_AT_RAIL_ORIGIN)}
                value={shipment.rail_loaded_at_origin}
                editable
                onSave={onUpdateRailEvent}
                param="rail_loaded_at_origin"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-51fe0b9b"
                className="mb-4 sm:col-span-1"
                title={t(DEPARTED_FROM_ORIGIN_RAIL)}
                key={t(DEPARTED_FROM_ORIGIN_RAIL)}
                value={shipment.rail_departed_from_origin}
                editable
                onSave={onUpdateRailEvent}
                param="rail_departed_from_origin"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-f12055f4"
                className="mb-4 sm:col-span-1"
                title={t(ARRIVED_AT_DESTINATION_RAIL_FACILITY)}
                key={t(ARRIVED_AT_DESTINATION_RAIL_FACILITY)}
                value={shipment.rail_arrived_at_destination_port_facility}
                editable
                onSave={onUpdateRailEvent}
                param="rail_arrived_at_destination_port_facility"
                useNamedUpdate
                timezone={timezone}
              />
              <ShipmentDetailDateItem
                dataTestId="ShipmentDetailDateItem-c5240fe8"
                className="mb-4 sm:col-span-1"
                title={t(UNLOADED_AT_DESTINATION_PORT)}
                key={t(UNLOADED_AT_DESTINATION_PORT)}
                value={shipment.rail_unloaded_at_destination_port}
                editable
                onSave={onUpdateRailEvent}
                param="rail_unloaded_at_destination_port"
                useNamedUpdate
                timezone={timezone}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CargomaticCollapsibleCard>

      {renderImportantDates()}

      {isIntermodalType
        ? getInterModalShipmentOrderAndAdditionalCards()
        : getNonInterModalDetailCard()}

      <CargomaticCollapsibleCard
        id="reference-numbers"
        dataTestId="CargomaticCard-14b65e21"
        className="mb-6"
        header={() => (
          <div
            data-testid="div-ca9c3fa1"
            className="flex items-center justify-between"
          >
            <div
              data-testid="div-3273e7d3"
              className="flex items-center space-x-1"
            >
              <h3 data-testid="h3-ec9ec100" className="my-0">
                {titleize(t('REFERENCE_NUMBERS'))}
              </h3>
              <TooltipProvider>
                <Tooltip id="tooltip-route-details">
                  <TooltipTrigger>
                    <div data-testid="div-d9f06124">
                      <InfoIcon size="medium" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <Translation>
                      {(t) => (
                        <p data-testid="p-adea810d">{t('REF_NUMS_NOTE')}</p>
                      )}
                    </Translation>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <TooltipProvider>
              <Tooltip id="tooltip-edit-references">
                <TooltipTrigger asChild>
                  <div data-testid="div-f1082c4d">
                    <EditButton
                      dataTestId="EditButton-4fc88b6f"
                      onClick={handleEditReferenceNumbers}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <Translation>
                    {(t) => (
                      <p data-testid="p-63a93803">
                        {t('CLICK_TO_EDIT_REF_NUMS')}
                      </p>
                    )}
                  </Translation>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      >
        <div data-testid="div-80758dcc" className="mb-4">
          {isIntermodalType && renderMissingReferenceNumbers()}
        </div>
        <div data-testid="div-9e93f28c" className="mb-4">
          {renderReferenceNumbers()}
        </div>
        <DescriptionListItem
          dataTestId="DescriptionListItem-d2c4ed8b"
          label={t('CUSTOMERS_ESTIMATE')}
          className="mb-4 sm:col-span-1"
        >
          {shipment.estimated_cost
            ? formatMoney(shipment.estimated_cost, shipment.currency)
            : titleize(t('UNKNOWN'))}
        </DescriptionListItem>
        <ShipmentDetailValueItem
          dataTestId="ShipmentDetailValueItem-c5270591"
          rows={4}
          title="Special instructions"
          info="Drivers can see these instructions in the mobile app"
          infoPosition="top"
          value={unescapeHTML(shipment.notes)}
          editable
          onSave={(value) => onNotesUpdate(shipment, value)}
        />
        <ShipmentDetailValueItem
          dataTestId="ShipmentDetailValueItem-023a9dce"
          rows={4}
          title={t('ADMIN_NOTES')}
          value={unescapeHTML(shipment.admin_notes)}
          editable
          onSave={(value) => onAdminNotesUpdate(shipment, value)}
        />
      </CargomaticCollapsibleCard>
      <CargomaticCollapsibleCard
        id="tags"
        dataTestId="CargomaticCard-0849d305"
        header={() => (
          <div className="flex items-center justify-between">
            <h3 className="my-0">{t('TAGS')}</h3>
          </div>
        )}
      >
        <TagEditor
          dataTestId="TagEditor-255f3edb"
          object={shipment}
          tags={tags}
          suggestions={Constants.shipmentTagSuggestions}
          handleChange={(_, tags) => {
            onTagsUpdate(shipment, mapTags(tags));
          }}
        />
      </CargomaticCollapsibleCard>
      <EditReferenceNumbersModal
        reference_numbers={shipment.reference_numbers}
        isIntermodalShipmentType={isIntermodalType}
        show={showEditReferenceNumbersModal}
        onSave={handleSaveReferenceNumbers}
        onClose={handleCloseEditReferenceNumbersModal}
        page="ShipmentDetails"
        shipper={shipment.shipper}
        requiredReferenceNumbers={missingReferenceNumbersList}
      />
    </div>
  );
};

ShipmentDetails.propTypes = {
  shipment: PropTypes.object.isRequired,
  audits: PropTypes.object,
  onReferenceNumbersChange: PropTypes.func.isRequired,
  onNotesUpdate: PropTypes.func.isRequired,
  onAdminNotesUpdate: PropTypes.func.isRequired,
  onTagsUpdate: PropTypes.func.isRequired,
  onUpdateEstimatedArrivalChange: PropTypes.func.isRequired,
  onUpdateArrivalDateChange: PropTypes.func.isRequired,
  onUpdateDischargeDateChange: PropTypes.func.isRequired,
  onUpdateCustomLfdChange: PropTypes.func.isRequired,
  onUpdateLastFreeDateChange: PropTypes.func.isRequired,
  onUpdateYardLastFreeDateChange: PropTypes.func.isRequired,
  onUpdateEmptyReturnedDateChange: PropTypes.func.isRequired,
  onUpdateEmptyReadyForPickupDateChange: PropTypes.func.isRequired,
  onUpdateLastCutDateChange: PropTypes.func.isRequired,
  onUpdateEarliestReturnDateChange: PropTypes.func.isRequired,
  onUpdateEarliestEmptyDateChange: PropTypes.func.isRequired,
  onUpdateOutgatedDateChange: PropTypes.func.isRequired,
  onUpdateCfsGateInChange: PropTypes.func.isRequired,
  onUpdateCfsGateOutChange: PropTypes.func.isRequired,
  onShipmentTypeChange: PropTypes.func.isRequired,
  onEquipmentTypeChange: PropTypes.func.isRequired,
  onExceptionExemptChange: PropTypes.func.isRequired,
  onUpdateRailEvent: PropTypes.func.isRequired,
  onPODValidChange: PropTypes.func.isRequired,
  onStackPositionUpdate: PropTypes.func.isRequired,
  updateMissingReferenceNumbers: PropTypes.func,
  onUpdateAvailableDateChange: PropTypes.func.isRequired,
  onUpdatePerDiemDateChange: PropTypes.func.isRequired,
  onUpdateDocumentCutoffDateChange: PropTypes.func.isRequired,
  onUpdateYardAvailableDateChange: PropTypes.func.isRequired,
};

export default ShipmentDetails;
